import * as React from "react";
import type {
  ComponentRegistry,
  ComponentRenderProps,
} from "@json-render/react";
import { useBoundProp } from "@json-render/react";
import { Button } from "@erp/ui/button";
import { Badge } from "@erp/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@erp/ui/card";
import { Input } from "@erp/ui/input";
import { AlertContainer, AlertDescription, AlertTitle } from "@erp/ui/alert";
import { Separator } from "@erp/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@erp/ui/table";
import {
  Sheet,
  SheetContentResizable,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@erp/ui/sheet";
import { parseChartSpec, VegaChart } from "@erp/viz";

/**
 * React implementations for the internal-UI catalog (see ./catalog.ts).
 *
 * Each renderer reads the already-resolved `element.props` that the json-render
 * `<Renderer>` passes in (state expressions like `{ $state }` are resolved
 * before they reach us) and maps them onto `@erp/ui` components + Tailwind
 * classes drawn from DESIGN.md. Props are coerced defensively so an imperfect
 * spec degrades gracefully instead of throwing.
 */

// Small class-name joiner so we don't pull a clsx dependency into this package.
function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asBool(value: unknown): boolean {
  return value === true;
}

function oneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

const GAP: Record<string, string> = {
  none: "gap-0",
  sm: "gap-2",
  md: "gap-3",
  lg: "gap-4",
  xl: "gap-6",
};

const PADDING: Record<string, string> = {
  none: "p-0",
  sm: "p-2",
  md: "p-3",
  lg: "p-4",
  xl: "p-6",
};

const ALIGN: Record<string, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
};

const JUSTIFY: Record<string, string> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
};

const TEXT_VARIANT: Record<string, string> = {
  body: "text-sm font-normal",
  label: "text-sm font-medium",
  muted: "text-sm font-light text-muted-foreground",
  eyebrow:
    "text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground",
  subtitle: "text-lg font-semibold",
};

const TEXT_TONE: Record<string, string> = {
  default: "",
  muted: "text-muted-foreground",
  destructive: "text-destructive",
  success: "text-green-600",
  warning: "text-amber-600",
};

const HEADING_LEVEL: Record<number, string> = {
  1: "text-3xl font-bold tracking-tight",
  2: "text-2xl font-bold",
  3: "text-xl font-semibold",
  4: "text-lg font-semibold",
};

const TEXT_ALIGN: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

function flexClasses(
  props: Record<string, unknown>,
  direction: "row" | "col",
): string {
  return cx(
    "flex",
    direction === "row" ? "flex-row" : "flex-col",
    GAP[asString(props.gap, "md")],
    props.padding ? PADDING[asString(props.padding)] : null,
    props.align ? ALIGN[asString(props.align)] : null,
    props.justify ? JUSTIFY[asString(props.justify)] : null,
    asBool(props.wrap) ? "flex-wrap" : null,
  );
}

function BoxRenderer({ element, children }: ComponentRenderProps) {
  const props = element.props;
  const direction = oneOf(props.direction, ["row", "col"] as const, "col");
  return <div className={flexClasses(props, direction)}>{children}</div>;
}

function StackRenderer({ element, children }: ComponentRenderProps) {
  return <div className={flexClasses(element.props, "col")}>{children}</div>;
}

function RowRenderer({ element, children }: ComponentRenderProps) {
  // Children share the width equally (flex-1) so a row of cards/charts fills the
  // container instead of left-packing at natural width. For natural-width inline
  // items (badges, label+value) use Box with direction="row".
  return (
    <div
      className={cx(
        flexClasses(element.props, "row"),
        "[&>*]:min-w-0 [&>*]:flex-1",
      )}
    >
      {children}
    </div>
  );
}

const GRID_COLS: Record<number, string> = {
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-2 lg:grid-cols-4",
};

function GridRenderer({ element, children }: ComponentRenderProps) {
  const props = element.props;
  const columns = oneOf(
    String(props.columns ?? 2),
    ["2", "3", "4"] as const,
    "2",
  );
  return (
    <div
      className={cx(
        "grid",
        GRID_COLS[Number(columns)],
        GAP[asString(props.gap, "md")],
      )}
    >
      {children}
    </div>
  );
}

function HeadingRenderer({ element }: ComponentRenderProps) {
  const props = element.props;
  const level = oneOf(
    String(props.level ?? 2),
    ["1", "2", "3", "4"] as const,
    "2",
  );
  const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4";
  return (
    <Tag className={HEADING_LEVEL[Number(level)]}>{asString(props.value)}</Tag>
  );
}

function TextRenderer({ element }: ComponentRenderProps) {
  const props = element.props;
  const variant = asString(props.variant, "body");
  const tone = asString(props.tone, "default");
  const align = asString(props.align);
  return (
    <p
      className={cx(
        TEXT_VARIANT[variant] ?? TEXT_VARIANT.body,
        TEXT_TONE[tone] ?? "",
        align ? TEXT_ALIGN[align] : null,
      )}
    >
      {asString(props.value)}
    </p>
  );
}

function CardRenderer({ element, children }: ComponentRenderProps) {
  const props = element.props;
  const title = asString(props.title);
  const description = asString(props.description);
  const hasHeader = title !== "" || description !== "";
  return (
    <Card>
      {hasHeader ? (
        <CardHeader>
          {title ? <CardTitle>{title}</CardTitle> : null}
          {description ? (
            <CardDescription>{description}</CardDescription>
          ) : null}
        </CardHeader>
      ) : null}
      <CardContent className="flex flex-col gap-3">{children}</CardContent>
    </Card>
  );
}

const BUTTON_VARIANTS = [
  "default",
  "destructive",
  "outline",
  "secondary",
  "ghost",
  "link",
] as const;
const BUTTON_SIZES = ["default", "sm", "lg"] as const;

function ButtonRenderer({ element, emit }: ComponentRenderProps) {
  const props = element.props;
  return (
    <Button
      variant={oneOf(props.variant, BUTTON_VARIANTS, "default")}
      size={oneOf(props.size, BUTTON_SIZES, "default")}
      disabled={asBool(props.disabled)}
      onClick={() => emit("press")}
    >
      {asString(props.label)}
    </Button>
  );
}

const BADGE_VARIANTS = [
  "default",
  "secondary",
  "destructive",
  "outline",
  "success",
  "warning",
] as const;

function BadgeRenderer({ element }: ComponentRenderProps) {
  const props = element.props;
  return (
    <Badge variant={oneOf(props.variant, BADGE_VARIANTS, "default")}>
      {asString(props.label)}
    </Badge>
  );
}

const INPUT_TYPES = ["text", "email", "number", "password"] as const;

function InputRenderer({ element, bindings }: ComponentRenderProps) {
  const props = element.props;
  const [value, setValue] = useBoundProp<string>(
    asString(props.value),
    bindings?.value,
  );
  return (
    <Input
      type={oneOf(props.type, INPUT_TYPES, "text")}
      placeholder={asString(props.placeholder)}
      disabled={asBool(props.disabled)}
      value={value ?? ""}
      onChange={(event) => setValue(event.target.value)}
    />
  );
}

function AlertRenderer({ element }: ComponentRenderProps) {
  const props = element.props;
  const variant = oneOf(
    props.variant,
    ["default", "destructive"] as const,
    "default",
  );
  return (
    <AlertContainer variant={variant}>
      {props.title ? <AlertTitle>{asString(props.title)}</AlertTitle> : null}
      {props.description ? (
        <AlertDescription>{asString(props.description)}</AlertDescription>
      ) : null}
    </AlertContainer>
  );
}

function SeparatorRenderer({ element }: ComponentRenderProps) {
  const orientation = oneOf(
    element.props.orientation,
    ["horizontal", "vertical"] as const,
    "horizontal",
  );
  return <Separator orientation={orientation} />;
}

interface TableColumn {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
}

function TableRenderer({ element }: ComponentRenderProps) {
  const props = element.props;
  // Shape is enforced by the catalog's Zod schema before render; coerce here so
  // a mid-edit spec doesn't crash the live preview.
  const columns: TableColumn[] = Array.isArray(props.columns)
    ? (props.columns as TableColumn[])
    : [];
  const rows: Array<Record<string, unknown>> = Array.isArray(props.rows)
    ? (props.rows as Array<Record<string, unknown>>)
    : [];

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHead
              key={column.key}
              className={column.align === "right" ? "text-right" : undefined}
            >
              {column.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, rowIndex) => (
          <TableRow key={rowIndex}>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                className={cx(
                  column.align === "right" ? "text-right tabular-nums" : null,
                )}
              >
                {asString(row[column.key], String(row[column.key] ?? ""))}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ChartRenderer({ element }: ComponentRenderProps) {
  const props = element.props;
  const title = asString(props.title);
  const height = typeof props.height === "number" ? props.height : 240;
  // parseChartSpec vets untrusted (AI-generated) specs: inline data only, no
  // remote loads. Same path the chat `render_chart` tool uses.
  const parsed = parseChartSpec(props.spec);

  return (
    <div className="w-full min-w-[280px] rounded-md border bg-card p-3">
      {title ? (
        <div className="mb-2 text-sm font-medium text-card-foreground">
          {title}
        </div>
      ) : null}
      {parsed.ok ? (
        <VegaChart spec={parsed.spec} height={height} />
      ) : (
        <p className="text-sm text-muted-foreground">
          Unable to render chart. {parsed.error}
        </p>
      )}
    </div>
  );
}

const SHEET_SIDES = ["left", "right", "top", "bottom"] as const;

function SheetRenderer({ element, children, bindings }: ComponentRenderProps) {
  const props = element.props;
  // `open` is two-way bound (via { $bindState }) so a press action can open the
  // sheet and the built-in close control / overlay can dismiss it.
  const [open, setOpen] = useBoundProp<boolean>(
    asBool(props.open),
    bindings?.open,
  );
  const side = oneOf(props.side, SHEET_SIDES, "right");
  const size = typeof props.size === "number" ? props.size : 42;
  const title = asString(props.title, "Details");
  const description = asString(props.description);

  return (
    <Sheet open={Boolean(open)} onOpenChange={(next) => setOpen(next)}>
      <SheetContentResizable side={side} resizableProps={{ defaultSize: size }}>
        <div className="flex h-full flex-col overflow-hidden">
          <SheetHeader className="border-b">
            <SheetTitle>{title}</SheetTitle>
            {description ? (
              <SheetDescription>{description}</SheetDescription>
            ) : null}
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-3">
            {children}
          </div>
        </div>
      </SheetContentResizable>
    </Sheet>
  );
}

/** Registry mapping every internal-UI catalog component to its `@erp/ui` renderer. */
export const internalUiComponents: ComponentRegistry = {
  Box: BoxRenderer,
  Stack: StackRenderer,
  Row: RowRenderer,
  Grid: GridRenderer,
  Heading: HeadingRenderer,
  Text: TextRenderer,
  Card: CardRenderer,
  Button: ButtonRenderer,
  Badge: BadgeRenderer,
  Input: InputRenderer,
  Alert: AlertRenderer,
  Separator: SeparatorRenderer,
  Table: TableRenderer,
  Chart: ChartRenderer,
  Sheet: SheetRenderer,
};
